import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  Bell, CheckCircle2, AlertTriangle, Info, XCircle, X
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationModal() {
  const [notification, setNotification] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    checkNotifications();
  }, []);

  const checkNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const unread = (res.data.notifications || []).filter(n => !n.read);
      if (unread.length > 0) {
        // Show the most recent unread notification
        setNotification(unread[0]);
        setVisible(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleClose = async () => {
    if (notification) {
      try {
        const token = localStorage.getItem("token");
        await axios.post(`${API}/notifications/${notification.id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        console.error(e);
      }
    }
    setVisible(false);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle2 className="w-16 h-16 text-[#0ECB81]" />;
      case 'warning': return <AlertTriangle className="w-16 h-16 text-[#F0B90B]" />;
      case 'urgent': return <XCircle className="w-16 h-16 text-[#F6465D]" />;
      default: return <Info className="w-16 h-16 text-[#3B82F6]" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'success': return 'border-[#0ECB81]';
      case 'warning': return 'border-[#F0B90B]';
      case 'urgent': return 'border-[#F6465D]';
      default: return 'border-[#3B82F6]';
    }
  };

  const getTypeBg = (type) => {
    switch (type) {
      case 'success': return 'from-[#0ECB81]/20';
      case 'warning': return 'from-[#F0B90B]/20';
      case 'urgent': return 'from-[#F6465D]/20';
      default: return 'from-[#3B82F6]/20';
    }
  };

  const getButtonColor = (type) => {
    switch (type) {
      case 'success': return 'bg-[#0ECB81] hover:bg-[#0ECB81]/80';
      case 'warning': return 'bg-[#F0B90B] hover:bg-[#F0B90B]/80 text-[#0B0E11]';
      case 'urgent': return 'bg-[#F6465D] hover:bg-[#F6465D]/80';
      default: return 'bg-[#3B82F6] hover:bg-[#3B82F6]/80';
    }
  };

  if (!visible || !notification) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-in fade-in duration-300">
      <div 
        className={`relative w-full max-w-xl bg-gradient-to-b ${getTypeBg(notification.type)} to-[#181A20] rounded-2xl border-2 ${getTypeColor(notification.type)} shadow-2xl animate-in zoom-in-95 duration-300`}
        data-testid="notification-modal"
      >
        {/* Close button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#2B3139] hover:bg-[#3B4149] flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5 text-[#848E9C]" />
        </button>

        <div className="p-8 text-center">
          {/* Icon */}
          <div className="mb-6 flex justify-center">
            <div className={`w-24 h-24 rounded-full bg-[#0B0E11] flex items-center justify-center border-2 ${getTypeColor(notification.type)}`}>
              {getTypeIcon(notification.type)}
            </div>
          </div>

          {/* Badge */}
          <div className="mb-4">
            {notification.type === 'urgent' && (
              <Badge className="bg-[#F6465D] text-white px-4 py-1 text-sm">URGENTE</Badge>
            )}
            {notification.type === 'warning' && (
              <Badge className="bg-[#F0B90B] text-[#0B0E11] px-4 py-1 text-sm">AVISO IMPORTANTE</Badge>
            )}
            {notification.type === 'success' && (
              <Badge className="bg-[#0ECB81] text-white px-4 py-1 text-sm">BUENAS NOTICIAS</Badge>
            )}
            {notification.type === 'info' && (
              <Badge className="bg-[#3B82F6] text-white px-4 py-1 text-sm">INFORMACIÓN</Badge>
            )}
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-[#EAECEF] mb-4">
            {notification.title}
          </h2>

          {/* Message */}
          <div className="bg-[#0B0E11] rounded-xl p-6 mb-6 border border-[#2B3139]">
            <p className="text-[#EAECEF] text-lg leading-relaxed whitespace-pre-wrap">
              {notification.message}
            </p>
          </div>

          {/* Date */}
          <p className="text-sm text-[#848E9C] mb-6">
            {new Date(notification.created_at).toLocaleDateString('es-ES', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>

          {/* Button */}
          <Button 
            onClick={handleClose}
            className={`w-full py-6 text-lg font-bold ${getButtonColor(notification.type)} text-white`}
            data-testid="notification-modal-close"
          >
            ENTENDIDO
          </Button>
        </div>
      </div>
    </div>
  );
}
